/**
 * Black Elm partner map (Mapbox GL).
 * Loads GeoJSON from public-wholesale-map edge function.
 *
 * Markup:
 *   <div class="be-partner-map"
 *        data-view="find-us|wholesale"
 *        data-program="all|coffee|pastry"
 *        data-api="https://…/functions/v1/public-wholesale-map">
 *     <div class="be-partner-map__canvas"></div>
 *     <aside class="be-partner-map__list" hidden></aside>
 *   </div>
 */
(function () {
  const MAPBOX_CSS = "https://api.mapbox.com/mapbox-gl-js/v3.9.4/mapbox-gl.css";
  const MAPBOX_JS = "https://api.mapbox.com/mapbox-gl-js/v3.9.4/mapbox-gl.js";
  // Same publishable anon key used by other public Shopify pages (menu, forms).
  const DEFAULT_ANON =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhwamp2cmpjam91eWxhcnR1cHd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk3MzQ3MTEsImV4cCI6MjA2NTMxMDcxMX0.OYbFjEfyO4IspmLKW8W9yqre-46GiqobE22yO7aUvLM";

  function readAnonKey() {
    var meta = document.querySelector('meta[name="supabase-anon-key"]');
    var fromMeta = meta ? meta.getAttribute("content") || "" : "";
    return window.BE_SUPABASE_ANON_KEY || fromMeta || DEFAULT_ANON;
  }

  function loadCss(href) {
    if (document.querySelector('link[href="' + href + '"]')) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.onload = function () {
        resolve();
      };
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }

  function loadScript(src) {
    if (window.mapboxgl) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = function () {
        resolve();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function categoryLabel(cat) {
    var map = {
      cafe: "Coffee shop",
      grocery: "Grocery",
      restaurant: "Restaurant",
      bakery: "Bakery partner",
      office: "Business",
      owned: "Black Elm café",
      other: "Partner",
    };
    return map[cat] || "Partner";
  }

  function normalizePrograms(programs) {
    if (Array.isArray(programs)) return programs;
    if (typeof programs === "string") {
      try {
        var parsed = JSON.parse(programs);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        /* ignore */
      }
      return programs
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
    }
    return [];
  }

  /** Show coffee retail + pastry when a partner is on both programs. */
  function partnerMetaLabel(p) {
    var cat = p.category;
    if (cat === "owned") return "Black Elm café";
    var programs = normalizePrograms(p.programs);
    var parts = [];
    if (programs.indexOf("coffee") >= 0) {
      if (cat === "grocery") parts.push("Retail beans");
      else if (cat === "cafe") parts.push("Coffee partner");
      else if (cat === "restaurant") parts.push("Restaurant");
      else if (cat === "office") parts.push("Office coffee");
      else parts.push("Coffee");
    }
    if (programs.indexOf("pastry") >= 0) parts.push("Pastry partner");
    if (parts.length) return parts.join(" · ");
    return categoryLabel(cat);
  }

  function formatAddress(p) {
    var parts = [p.address, [p.city, p.state].filter(Boolean).join(", "), p.postalCode].filter(
      Boolean
    );
    return parts.join(" · ");
  }

  function initRoot(root) {
    var canvas = root.querySelector(".be-partner-map__canvas");
    var listEl = root.querySelector(".be-partner-map__list");
    var statusEl = root.querySelector(".be-partner-map__status");
    if (!canvas) return;

    var view = root.getAttribute("data-view") || "find-us";
    var program = root.getAttribute("data-program") || "all";
    var apiBase =
      root.getAttribute("data-api") ||
      "https://xpjjvrjcjouylartupwy.supabase.co/functions/v1/public-wholesale-map";

    if (statusEl) statusEl.textContent = "Loading map…";

    var url =
      apiBase +
      "?view=" +
      encodeURIComponent(view) +
      "&program=" +
      encodeURIComponent(program) +
      "&include_heat=1";

    var anon = readAnonKey();
    fetch(url, {
      headers: {
        apikey: anon,
        Authorization: "Bearer " + anon,
      },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (!data || !data.features) {
          throw new Error((data && data.error) || "bad_response");
        }
        var token = (data.meta && data.meta.mapboxToken) || window.BE_MAPBOX_TOKEN;
        if (!token) {
          throw new Error("missing_mapbox_token");
        }
        return loadCss(MAPBOX_CSS)
          .then(function () {
            return loadScript(MAPBOX_JS);
          })
          .then(function () {
            renderMap(root, canvas, listEl, statusEl, data, token, view);
          });
      })
      .catch(function (err) {
        console.warn("[be-partner-map]", err);
        if (statusEl) {
          statusEl.textContent =
            "Map is temporarily unavailable. Check back soon, or email wholesale@blackelm.coffee.";
        }
      });
  }

  function renderMap(root, canvas, listEl, statusEl, data, token, view) {
    window.mapboxgl.accessToken = token;

    var map = new window.mapboxgl.Map({
      container: canvas,
      style: "mapbox://styles/mapbox/light-v11",
      center: [-86.7, 36.2],
      zoom: 7,
      attributionControl: true,
    });
    map.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", function () {
      map.addSource("partners", {
        type: "geojson",
        data: { type: "FeatureCollection", features: data.features || [] },
        cluster: true,
        clusterMaxZoom: 12,
        clusterRadius: 48,
      });

      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "partners",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#2fa188",
          "circle-radius": ["step", ["get", "point_count"], 16, 10, 22, 30, 28],
          "circle-opacity": 0.85,
        },
      });

      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "partners",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": 12,
        },
        paint: { "text-color": "#ffffff" },
      });

      map.addLayer({
        id: "unclustered",
        type: "circle",
        source: "partners",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": [
            "match",
            ["get", "category"],
            "owned",
            "#1c1917",
            "grocery",
            "#0ea5e9",
            "bakery",
            "#d97706",
            "#2fa188",
          ],
          "circle-radius": 7,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });

      // Heat stub — source exists; layer only if enabled with features
      var heat = data.heat || {};
      map.addSource("dtc-heat", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: heat.enabled ? heat.features || [] : [],
        },
      });
      if (heat.enabled && heat.features && heat.features.length) {
        map.addLayer(
          {
            id: "dtc-heat-layer",
            type: "heatmap",
            source: "dtc-heat",
            maxzoom: 12,
            paint: {
              "heatmap-weight": [
                "interpolate",
                ["linear"],
                ["get", "orderCount"],
                1,
                0.2,
                50,
                1,
              ],
              "heatmap-intensity": 1,
              "heatmap-radius": 28,
              "heatmap-opacity": 0.55,
            },
          },
          "clusters"
        );
      }

      if (data.features && data.features.length) {
        var bounds = new window.mapboxgl.LngLatBounds();
        data.features.forEach(function (f) {
          bounds.extend(f.geometry.coordinates);
        });
        map.fitBounds(bounds, { padding: 48, maxZoom: 10 });
      }

      if (statusEl) {
        var n = (data.features || []).length;
        statusEl.textContent =
          view === "wholesale"
            ? n + " wholesale partners on the map"
            : n + " places to find Black Elm";
      }

      if (listEl) {
        renderList(listEl, data.features || [], map);
        listEl.hidden = false;
      }
    });

    map.on("click", "clusters", function (e) {
      var features = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
      var clusterId = features[0].properties.cluster_id;
      map.getSource("partners").getClusterExpansionZoom(clusterId, function (err, zoom) {
        if (err) return;
        map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
      });
    });

    map.on("click", "unclustered", function (e) {
      var f = e.features[0];
      var p = f.properties;
      var html =
        "<strong>" +
        escapeHtml(p.name) +
        "</strong><br><span>" +
        escapeHtml(partnerMetaLabel(p)) +
        "</span>";
      if (p.address || p.city) {
        html +=
          "<br><span>" +
          escapeHtml(
            formatAddress({
              address: p.address,
              city: p.city,
              state: p.state,
              postalCode: p.postalCode,
            })
          ) +
          "</span>";
      }
      if (p.website) {
        html +=
          '<br><a href="' +
          escapeAttr(p.website) +
          '" target="_blank" rel="noopener">Website</a>';
      }
      new window.mapboxgl.Popup({ offset: 12 })
        .setLngLat(f.geometry.coordinates)
        .setHTML(html)
        .addTo(map);
    });

    map.on("mouseenter", "clusters", function () {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "clusters", function () {
      map.getCanvas().style.cursor = "";
    });
    map.on("mouseenter", "unclustered", function () {
      map.getCanvas().style.cursor = "pointer";
    });
    map.on("mouseleave", "unclustered", function () {
      map.getCanvas().style.cursor = "";
    });

    root._bePartnerMap = map;
  }

  function renderList(listEl, features, map) {
    var sorted = features.slice().sort(function (a, b) {
      return String(a.properties.name).localeCompare(String(b.properties.name));
    });
    var ul = document.createElement("ul");
    ul.className = "be-partner-map__list-items";
    sorted.forEach(function (f) {
      var p = f.properties;
      var li = document.createElement("li");
      li.className = "be-partner-map__list-item";
      li.innerHTML =
        "<button type=\"button\" class=\"be-partner-map__list-btn\">" +
        "<span class=\"be-partner-map__list-name\">" +
        escapeHtml(p.name) +
        "</span>" +
        "<span class=\"be-partner-map__list-meta\">" +
        escapeHtml(partnerMetaLabel(p)) +
        (p.city ? " · " + escapeHtml(p.city) : "") +
        "</span></button>";
      li.querySelector("button").addEventListener("click", function () {
        map.flyTo({ center: f.geometry.coordinates, zoom: 13 });
        new window.mapboxgl.Popup({ offset: 12 })
          .setLngLat(f.geometry.coordinates)
          .setHTML(
            "<strong>" +
              escapeHtml(p.name) +
              "</strong><br><span>" +
              escapeHtml(partnerMetaLabel(p)) +
              "</span>"
          )
          .addTo(map);
      });
      ul.appendChild(li);
    });
    listEl.innerHTML = "";
    listEl.appendChild(ul);
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, "&#39;");
  }

  function boot() {
    document.querySelectorAll(".be-partner-map").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
